import Link from 'next/link';
import Button from "../ui/Button";
import MarkerPinIcon from '@/components/icons/MarkerPinIcon';
import FileLinesIcon from '@/components/icons/FileLinesIcon';
import ArrowNarrowRightIcon from '@/components/icons/ArrowNarrowRightIcon';
import { type CareerJob } from '@/lib/careers-jobs';

export function JobCard({ job }: { job: CareerJob }) {
  return (
    <article
      className="group relative overflow-hidden rounded-2xl border border-neutral-10 bg-constant-white p-5 transition-colors duration-200 hover:border-blue-15 sm:p-6"
      aria-labelledby={`job-${job.id}-title`}
    >
      {/* Decorative left accent, shows on hover */}
      <span
        className="pointer-events-none absolute left-0 top-6 bottom-6 w-0.5 origin-top scale-y-0 rounded-full bg-blue-80 transition-transform duration-200 group-hover:scale-y-100"
        aria-hidden
      />

      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          {job.category ? (
            <span className="inline-flex h-6 items-center rounded-full bg-blue-5 px-2.5 text-[12px] font-semibold text-blue-60">
              {job.category}
            </span>
          ) : null}
          <h2
            id={`job-${job.id}-title`}
            className="mt-2 text-h4-mobile font-bold text-blue-100 transition-colors group- sm:text-h4-desktop"
          >
            {job.title}
          </h2>
        </div>
      </header>

      <p className="mt-3 line-clamp-2 text-body-md font-normal leading-relaxed text-text-secondary sm:line-clamp-3 sm:text-body-16-desktop">
        {job.description}
      </p>

      <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div className="flex flex-wrap items-center gap-1.5">
          {job.location ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-10 bg-bg-1 px-2.5 py-1 text-body-12-desktop font-semibold text-text-primary">
              <MarkerPinIcon size={13} color="#727FC3" className="shrink-0" aria-hidden />
              {job.location}
            </span>
          ) : null}
          {job.employmentType ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-10 bg-bg-1 px-2.5 py-1 text-body-12-desktop font-semibold text-text-primary">
              <FileLinesIcon size={13} color="#727FC3" className="shrink-0" aria-hidden />
              {job.employmentType}
            </span>
          ) : null}
        </div>

        <Button asChild variant="brand" size="md" className="w-full sm:w-auto">
          <Link href={`/careers/${job.id}`} aria-label={`Apply now for ${job.title}`}>
            <span className='flex items-center gap-1'>
              <span>Apply now</span>
              <ArrowNarrowRightIcon size={14} color="currentColor" className="transition-transform" />
            </span>
          </Link>
        </Button>
      </div>
    </article>
  );
}

export default function CareersJobListingsSection({ jobs }: { jobs?: CareerJob[] }) {
  const list = jobs || [];
  return (
    <section
      className="w-full rounded-2xl bg-neutral-5 p-4 py-12 sm:rounded-2xl sm:p-6 sm:py-14 lg:p-8"
      aria-labelledby="careers-open-roles-heading"
    >
      <h2 id="careers-open-roles-heading" className="text-h3-mobile font-bold text-text-primary sm:text-h3-desktop">
        Open roles
      </h2>
      <p className="mt-2 text-body-md text-text-secondary sm:text-body-16-desktop">
        Browse current opportunities and reach out, we&apos;ll get back to you as soon as we can.
      </p>

      {list.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-neutral-10 bg-constant-white p-10 text-center">
          <p className="text-h4-mobile font-bold text-text-primary sm:text-h4-desktop">No open roles right now</p>
          <p className="mt-2 text-body-md text-text-secondary">
            Check back soon, new opportunities open up all the time.
          </p>
        </div>
      ) : (
        <ul className="mt-8 flex w-full list-none flex-col gap-4 p-0 sm:mt-10 sm:gap-6">
          {list.map((job) => (
            <li key={job.id} className="w-full">
              <JobCard job={job} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
